import type { AiListingAutofillResponse } from '../../types/aiListingAutofill';
import type { PropertyAdInProgressAutosaveFields } from '../../types/propertyAdEditor';

import { PROPERTY_AD_AMENITY_FIELDS } from './propertyAdAmenities';

const PROPERTY_FIELDS = [
  'propertyKind',
  'completionStatus',
  'builtUpArea',
  'occupancy',
  'view',
  'transactionType',
  'beds',
  'baths',
  'floor',
  'furniture',
  'renovation',
] as const satisfies ReadonlyArray<keyof AiListingAutofillResponse['property']>;

const PROPERTY_AD_FIELDS = [
  'title',
  'description',
  'activityKind',
  'rentedTill',
  'sellingPrice',
  'multilisting',
  'currency',
  'price',
  'toSeller',
  'toDeveloper',
  'postHandover',
  'topUp',
  'topUpConditions',
  'priceYearly',
  'priceMonthly',
  'priceWeekly',
  'priceDaily',
  'defaultRentFrequency',
  'addCommission',
  'currencyCommission',
] as const satisfies ReadonlyArray<keyof AiListingAutofillResponse['propertyAd']>;

const pickDefined = <T extends object, K extends keyof T>(
  source: T,
  fields: readonly K[]
): Partial<Pick<T, K>> => {
  const result: Partial<Pick<T, K>> = {};
  fields.forEach((field) => {
    if (source[field] !== undefined) result[field] = source[field];
  });
  return result;
};

const toCoordinateString = (value: string | number | null | undefined): string | undefined => {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : undefined;
  if (typeof value !== 'string') return undefined;

  const coordinate = value.trim();
  return coordinate && Number.isFinite(Number(coordinate)) ? coordinate : undefined;
};

export const buildAiListingFormPatch = (
  response: AiListingAutofillResponse
): PropertyAdInProgressAutosaveFields => {
  const patch: PropertyAdInProgressAutosaveFields = {
    ...pickDefined(response.property, PROPERTY_FIELDS),
    ...pickDefined(response.property, PROPERTY_AD_AMENITY_FIELDS),
    ...pickDefined(response.propertyAd, PROPERTY_AD_FIELDS),
  };

  if (response.property.view !== undefined) patch.view = [...response.property.view];

  const handover = response.propertyAd.handover;
  if (handover !== undefined) {
    patch.handover = { quarter: handover.quarter, year: String(handover.year) };
  }

  const { status, resolved } = response.location;
  if (status === 'matched' && resolved?.locationId != null) {
    const locationId = String(resolved.locationId).trim();
    if (locationId) {
      patch.locationId = locationId;
      if (resolved.addressEn) patch.addressEn = resolved.addressEn;
      if (resolved.nameEn) patch.nameEn = resolved.nameEn;

      const latitude = toCoordinateString(resolved.coordinatesLat);
      const longitude = toCoordinateString(resolved.coordinatesLong);
      if (latitude !== undefined) patch.coordinatesLat = latitude;
      if (longitude !== undefined) patch.coordinatesLong = longitude;
    }
  }

  return patch;
};
